/**
 * ROI Calculator with Investment Recovery Graph
 *
 * Calculates AI automation ROI based on employee time savings
 * Shows interactive graph of investment recovery timeline
 */

import {
  ArrowLeft,
  Calculator,
  Plus,
  Trash2,
  Clock,
  DollarSign,
  Calendar,
  TrendingUp,
} from "lucide-react";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Legend,
} from "recharts";
import { v4 as uuidv4 } from "uuid";

import { PageSEO } from "@/components/SEO/PageSEO";
import { analytics } from "@/utils/analytics";

// Types
interface Employee {
  id: string;
  hourlyWage: number;
  hoursSavedPerDay: number;
  numberOfEmployees: number;
}

const ROICalculator: React.FC = () => {
  const navigate = useNavigate();

  // State
  const [projectPrice, setProjectPrice] = useState(50000);
  const [employees, setEmployees] = useState<Employee[]>([
    {
      id: uuidv4(),
      hourlyWage: 25,
      hoursSavedPerDay: 2,
      numberOfEmployees: 5,
    },
  ]);

  const [graphData, setGraphData] = useState<{ day: number; value: number }[]>(
    [],
  );
  const [breakEvenDay, setBreakEvenDay] = useState(0);
  const [dailySavings, setDailySavings] = useState(0);
  const [monthlySavings, setMonthlySavings] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile viewport for responsive chart margins
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Calculations with debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      calculateROI();
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [projectPrice, employees]);

  const calculateROI = () => {
    // Calculate total daily savings across all employees
    const dailySaving = employees.reduce(
      (total, emp) =>
        total + emp.hourlyWage * emp.hoursSavedPerDay * emp.numberOfEmployees,
      0,
    );

    if (dailySaving === 0) {
      setGraphData([]);
      setBreakEvenDay(0);
      setDailySavings(0);
      setMonthlySavings(0);
      return;
    }

    const monthlySaving = dailySaving * 22; // 22 working days per month

    // Calculate break-even point
    const breakEvenDays = Math.ceil(projectPrice / dailySaving);

    // IMPORTANT: Limit data points to prevent browser freeze
    // Maximum 730 days (2 years) to keep graph performant
    const MAX_DATA_POINTS = 730;
    const idealDataPoints = Math.max(365, Math.ceil(breakEvenDays * 2));
    const dataPoints = Math.min(idealDataPoints, MAX_DATA_POINTS);

    const newData = Array.from({ length: dataPoints }, (_, i) => ({
      day: i + 1,
      value: dailySaving * (i + 1) - projectPrice,
    }));

    setGraphData(newData);
    setBreakEvenDay(breakEvenDays);
    setDailySavings(dailySaving);
    setMonthlySavings(monthlySaving);

    // Track calculator usage
    analytics.trackToolUsage("ROI Calculator", "Calculation Complete", {
      projectPrice,
      breakEvenDays,
      dailySavings: dailySaving,
      monthlySavings: monthlySaving,
      numberOfEmployees: employees.reduce(
        (total, emp) => total + emp.numberOfEmployees,
        0,
      ),
      value: breakEvenDays,
    });
  };

  // Employee management
  const handleEmployeeChange = (
    id: string,
    field: keyof Omit<Employee, "id">,
    value: string,
  ) => {
    const numValue = parseFloat(value) || 0;
    setEmployees(
      employees.map((emp) =>
        emp.id === id ? { ...emp, [field]: numValue } : emp,
      ),
    );
  };

  const handleAddEmployee = () => {
    setEmployees([
      ...employees,
      {
        id: uuidv4(),
        hourlyWage: 25,
        hoursSavedPerDay: 2,
        numberOfEmployees: 1,
      },
    ]);
  };

  const handleRemoveEmployee = (id: string) => {
    if (employees.length > 1) {
      setEmployees(employees.filter((emp) => emp.id !== id));
    }
  };

  // Format helpers
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDays = (days: number) => {
    if (days <= 0) return "N/A";
    if (days < 30) return `${days} days`;
    if (days < 365) return `${Math.ceil(days / 30)} months`;
    return `${(days / 365).toFixed(1)} years`;
  };

  const finalValue = graphData[graphData.length - 1]?.value || 0;
  const finalDay = graphData.length;

  return (
    <>
      <PageSEO
        title="ROI Calculator"
        description="Calculate how quickly your AI automation investment pays for itself. See a visual timeline of your break-even point and long-term ROI based on employee time savings."
        keywords="ROI calculator, automation ROI, investment recovery, AI automation savings, employee time savings calculator, break-even analysis"
        ogImage="/og-image-tools.png"
      />

      <div className="min-h-screen bg-slate-50">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-12">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate("/tools")}
              className="flex items-center gap-2 text-slate-600 hover:text-[#B87333] mb-6 transition-colors group"
            >
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
              <span className="font-semibold">Back to Tools</span>
            </button>

            <div className="text-center mb-2">
              <p className="text-sm uppercase tracking-widest text-slate-500 font-semibold mb-4">
                AI Automation ROI
              </p>
              <h1 className="text-5xl font-black text-slate-900 leading-tight mb-6">
                Investment Recovery Calculator
              </h1>
              <p className="text-xl text-slate-600 max-w-[800px] mx-auto">
                Calculate how quickly your AI automation investment pays for
                itself based on employee time savings
              </p>
            </div>
          </div>

          {/* Mobile layout: Calculator Form → Metrics → Graph */}
          {/* Desktop layout: Metrics on top, then Form (left) + Graph (right) */}

          {/* Calculator Form - shown first on mobile, inside grid on desktop */}
          <div className="lg:hidden mb-8">
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-[#B87333] bg-opacity-20 rounded-lg">
                  <Calculator className="w-6 h-6 text-[#B87333]" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">
                  Calculator
                </h2>
              </div>

              {/* Project Price */}
              <div className="mb-8">
                <label
                  htmlFor="projectPriceMobile"
                  className="block text-sm font-semibold text-slate-700 mb-2"
                >
                  Project Investment ($)
                </label>
                <input
                  id="projectPriceMobile"
                  type="number"
                  value={projectPrice}
                  onChange={(e) =>
                    setProjectPrice(parseFloat(e.target.value) || 0)
                  }
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-[#B87333] focus:outline-none text-lg font-semibold transition-colors"
                  min="0"
                  step="1000"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Total cost of AI automation implementation
                </p>
              </div>

              {/* Employees Section */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-slate-900">
                    Employee Groups
                  </h3>
                  <button
                    type="button"
                    onClick={handleAddEmployee}
                    className="flex items-center gap-2 px-3 py-2 bg-[#B87333] text-white rounded-lg hover:bg-[#A67329] transition-colors text-sm font-semibold"
                  >
                    <Plus className="w-4 h-4" />
                    Add Group
                  </button>
                </div>

                <div className="space-y-6">
                  {employees.map((employee, index) => (
                    <div
                      key={employee.id}
                      className="p-4 bg-slate-50 rounded-lg border-2 border-slate-200 relative"
                    >
                      {employees.length > 1 && (
                        <button
                          onClick={() => handleRemoveEmployee(employee.id)}
                          className="absolute top-2 right-2 p-1 text-slate-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}

                      <h4 className="text-sm font-bold text-slate-700 mb-3">
                        Group {index + 1}
                      </h4>

                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-semibold text-slate-600 mb-1">
                            Number of Employees
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={employee.numberOfEmployees}
                            onChange={(e) =>
                              handleEmployeeChange(
                                employee.id,
                                "numberOfEmployees",
                                e.target.value,
                              )
                            }
                            className="w-full px-3 py-2 border border-slate-300 rounded focus:border-[#B87333] focus:outline-none"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-slate-600 mb-1">
                            Hourly Wage ($)
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.5"
                            value={employee.hourlyWage}
                            onChange={(e) =>
                              handleEmployeeChange(
                                employee.id,
                                "hourlyWage",
                                e.target.value,
                              )
                            }
                            className="w-full px-3 py-2 border border-slate-300 rounded focus:border-[#B87333] focus:outline-none"
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-semibold text-slate-600 mb-1">
                            Hours Saved Per Day
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.25"
                            value={employee.hoursSavedPerDay}
                            onChange={(e) =>
                              handleEmployeeChange(
                                employee.id,
                                "hoursSavedPerDay",
                                e.target.value,
                              )
                            }
                            className="w-full px-3 py-2 border border-slate-300 rounded focus:border-[#B87333] focus:outline-none"
                          />
                        </div>
                      </div>

                      <div className="mt-3 pt-3 border-t border-slate-300">
                        <p className="text-xs text-slate-600">
                          Daily savings:{" "}
                          <span className="font-bold text-green-600">
                            {formatCurrency(
                              employee.hourlyWage *
                                employee.hoursSavedPerDay *
                                employee.numberOfEmployees,
                            )}
                          </span>
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Metrics Cards - always 3 in a row */}
          <div className="grid grid-cols-3 gap-2 sm:gap-6 mb-8">
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-3 sm:p-6 border-2 border-transparent hover:border-[#B87333] transition-all">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <div className="p-1.5 sm:p-3 bg-blue-100 rounded-lg">
                  <Clock className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600" />
                </div>
                <h3 className="text-[10px] sm:text-sm font-semibold text-slate-600 uppercase tracking-wide">
                  Daily Savings
                </h3>
              </div>
              <p className="text-lg sm:text-4xl font-black text-blue-600">
                {formatCurrency(dailySavings)}
              </p>
              <p className="text-[10px] sm:text-sm text-slate-500 mt-1 sm:mt-2">
                Per working day
              </p>
            </div>

            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-3 sm:p-6 border-2 border-transparent hover:border-[#B87333] transition-all">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <div className="p-1.5 sm:p-3 bg-green-100 rounded-lg">
                  <DollarSign className="w-4 h-4 sm:w-6 sm:h-6 text-green-600" />
                </div>
                <h3 className="text-[10px] sm:text-sm font-semibold text-slate-600 uppercase tracking-wide">
                  Monthly Savings
                </h3>
              </div>
              <p className="text-lg sm:text-4xl font-black text-green-600">
                {formatCurrency(monthlySavings)}
              </p>
              <p className="text-[10px] sm:text-sm text-slate-500 mt-1 sm:mt-2">
                22 working days
              </p>
            </div>

            <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-3 sm:p-6 border-2 border-transparent hover:border-[#B87333] transition-all">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <div className="p-1.5 sm:p-3 bg-[#B87333] bg-opacity-20 rounded-lg">
                  <Calendar className="w-4 h-4 sm:w-6 sm:h-6 text-[#B87333]" />
                </div>
                <h3 className="text-[10px] sm:text-sm font-semibold text-slate-600 uppercase tracking-wide">
                  Break-Even
                </h3>
              </div>
              <p className="text-lg sm:text-4xl font-black text-[#B87333]">
                {formatDays(breakEvenDay)}
              </p>
              <p className="text-[10px] sm:text-sm text-slate-500 mt-1 sm:mt-2">
                {breakEvenDay} working days
              </p>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column: Input Form - hidden on mobile (shown above instead) */}
            <div className="hidden lg:block lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-xl p-8 sticky top-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 bg-[#B87333] bg-opacity-20 rounded-lg">
                    <Calculator className="w-6 h-6 text-[#B87333]" />
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900">
                    Calculator
                  </h2>
                </div>

                {/* Project Price */}
                <div className="mb-8">
                  <label
                    htmlFor="projectPrice"
                    className="block text-sm font-semibold text-slate-700 mb-2"
                  >
                    Project Investment ($)
                  </label>
                  <input
                    id="projectPrice"
                    type="number"
                    value={projectPrice}
                    onChange={(e) =>
                      setProjectPrice(parseFloat(e.target.value) || 0)
                    }
                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-lg focus:border-[#B87333] focus:outline-none text-lg font-semibold transition-colors"
                    min="0"
                    step="1000"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Total cost of AI automation implementation
                  </p>
                </div>

                {/* Employees Section */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-slate-900">
                      Employee Groups
                    </h3>
                    <button
                      type="button"
                      onClick={handleAddEmployee}
                      className="flex items-center gap-2 px-3 py-2 bg-[#B87333] text-white rounded-lg hover:bg-[#A67329] transition-colors text-sm font-semibold"
                    >
                      <Plus className="w-4 h-4" />
                      Add Group
                    </button>
                  </div>

                  <div className="space-y-6">
                    {employees.map((employee, index) => (
                      <div
                        key={employee.id}
                        className="p-4 bg-slate-50 rounded-lg border-2 border-slate-200 relative"
                      >
                        {employees.length > 1 && (
                          <button
                            onClick={() => handleRemoveEmployee(employee.id)}
                            className="absolute top-2 right-2 p-1 text-slate-400 hover:text-red-500 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}

                        <h4 className="text-sm font-bold text-slate-700 mb-3">
                          Group {index + 1}
                        </h4>

                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1">
                              Number of Employees
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={employee.numberOfEmployees}
                              onChange={(e) =>
                                handleEmployeeChange(
                                  employee.id,
                                  "numberOfEmployees",
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 border border-slate-300 rounded focus:border-[#B87333] focus:outline-none"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1">
                              Hourly Wage ($)
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="0.5"
                              value={employee.hourlyWage}
                              onChange={(e) =>
                                handleEmployeeChange(
                                  employee.id,
                                  "hourlyWage",
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 border border-slate-300 rounded focus:border-[#B87333] focus:outline-none"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-semibold text-slate-600 mb-1">
                              Hours Saved Per Day
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="0.25"
                              value={employee.hoursSavedPerDay}
                              onChange={(e) =>
                                handleEmployeeChange(
                                  employee.id,
                                  "hoursSavedPerDay",
                                  e.target.value,
                                )
                              }
                              className="w-full px-3 py-2 border border-slate-300 rounded focus:border-[#B87333] focus:outline-none"
                            />
                          </div>
                        </div>

                        <div className="mt-3 pt-3 border-t border-slate-300">
                          <p className="text-xs text-slate-600">
                            Daily savings:{" "}
                            <span className="font-bold text-green-600">
                              {formatCurrency(
                                employee.hourlyWage *
                                  employee.hoursSavedPerDay *
                                  employee.numberOfEmployees,
                              )}
                            </span>
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Graph */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <TrendingUp className="w-6 h-6 text-blue-600" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                    Investment Recovery Timeline
                  </h2>
                </div>

                {graphData.length > 0 ? (
                  <>
                    <div className="h-[350px] sm:h-[500px] w-full mb-6">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={graphData}
                          margin={
                            isMobile
                              ? { top: 10, right: 10, left: 20, bottom: 40 }
                              : { top: 20, right: 30, left: 80, bottom: 60 }
                          }
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#e5e7eb"
                          />
                          <XAxis
                            dataKey="day"
                            label={{
                              value: "Days",
                              position: "bottom",
                              offset: 10,
                              style: {
                                fontSize: isMobile ? 11 : 14,
                                fontWeight: 600,
                                fill: "#64748b",
                              },
                            }}
                            tick={{
                              fontSize: isMobile ? 10 : 12,
                              fill: "#64748b",
                            }}
                          />
                          <YAxis
                            label={
                              isMobile
                                ? undefined
                                : {
                                    value: "Return on Investment ($)",
                                    angle: -90,
                                    position: "insideLeft",
                                    offset: 20,
                                    style: {
                                      fontSize: 14,
                                      fontWeight: 600,
                                      fill: "#64748b",
                                      textAnchor: "middle",
                                    },
                                  }
                            }
                            tick={{
                              fontSize: isMobile ? 10 : 12,
                              fill: "#64748b",
                            }}
                            tickFormatter={(value) =>
                              `$${(value / 1000).toFixed(0)}k`
                            }
                            width={isMobile ? 45 : undefined}
                          />
                          <Tooltip
                            formatter={(value?: number) => [
                              formatCurrency(value ?? 0),
                              "ROI",
                            ]}
                            labelFormatter={(label?: number) => `Day ${label}`}
                            contentStyle={{
                              backgroundColor: "white",
                              border: "2px solid #B87333",
                              borderRadius: "8px",
                              padding: "12px",
                            }}
                          />
                          <Legend
                            verticalAlign="top"
                            height={36}
                            iconType="line"
                            wrapperStyle={{
                              fontSize: isMobile ? 12 : 14,
                              fontWeight: 600,
                            }}
                          />
                          <ReferenceLine
                            y={0}
                            stroke="#64748b"
                            strokeDasharray="5 5"
                            strokeWidth={2}
                            label={
                              isMobile
                                ? undefined
                                : {
                                    value: "Break-Even Point",
                                    position: "right",
                                    style: {
                                      fontSize: 12,
                                      fontWeight: 600,
                                      fill: "#B87333",
                                    },
                                  }
                            }
                          />
                          <Line
                            type="monotone"
                            dataKey="value"
                            name="Investment Return"
                            stroke="#B87333"
                            strokeWidth={3}
                            dot={false}
                            activeDot={{
                              r: 6,
                              fill: "#B87333",
                              stroke: "white",
                              strokeWidth: 2,
                            }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Summary Stats */}
                    <div className="grid grid-cols-2 gap-4 p-4 sm:p-6 bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl border-2 border-slate-200">
                      <div>
                        <p className="text-sm text-slate-600 mb-1">
                          Break-Even Point
                        </p>
                        <p className="text-xl sm:text-2xl font-black text-[#B87333]">
                          Day {breakEvenDay}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          Investment fully recovered in{" "}
                          {formatDays(breakEvenDay)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600 mb-1">
                          Total Return ({finalDay} days)
                        </p>
                        <p className="text-xl sm:text-2xl font-black text-green-600">
                          {formatCurrency(finalValue)}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          Net profit after investment recovery
                        </p>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="h-[300px] sm:h-[400px] flex items-center justify-center bg-slate-50 rounded-xl border-2 border-dashed border-slate-300">
                    <div className="text-center">
                      <Calculator className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                      <p className="text-lg font-semibold text-slate-500">
                        Enter your project details to see the ROI timeline
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="mt-12 bg-gradient-to-r from-slate-900 to-slate-800 rounded-2xl shadow-2xl p-12 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Start Your AI Automation Journey?
            </h2>
            <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
              Let's discuss how we can help you achieve these results (and more)
              with a custom AI automation solution tailored to your business
              needs.
            </p>
            <button
              onClick={() => {
                // Track conversion before opening calendar
                analytics.conversion("consultation_requested", 1, {
                  source: "roi_calculator",
                  projectPrice,
                  breakEvenDays: breakEvenDay,
                  dailySavings,
                  page: "/tools/roi-calculator",
                });

                window.open(
                  "https://calendar.notion.so/meet/aigeniusconsulting/793ez04oz7",
                  "_blank",
                );
              }}
              className="bg-[#B87333] hover:bg-[#A67329] text-white px-10 py-4 rounded-lg font-bold text-lg transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105"
            >
              Schedule a Free Consultation
            </button>
          </div>

          {/* Assumptions */}
          <div className="mt-8 p-6 bg-slate-100 rounded-xl">
            <h3 className="text-sm font-bold text-slate-700 mb-3 uppercase tracking-wide">
              Calculation Assumptions
            </h3>
            <ul className="text-sm text-slate-600 space-y-2">
              <li>• Monthly savings based on 22 working days per month</li>
              <li>
                • Break-even calculated as: Project Investment ÷ Daily Savings
              </li>
              <li>
                • Daily savings calculated as: Sum of (Hourly Wage × Hours Saved
                × Number of Employees)
              </li>
              <li>
                • Graph shows investment recovery timeline up to {finalDay} days
              </li>
              <li>
                • Values reflect labor cost savings only; additional benefits
                may apply
              </li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
};

export default ROICalculator;
